import React, { useState, useCallback, useEffect } from 'react';
import { useAgentStream, AgentUsage } from '../../hooks/useAgentStream';
import { UsageDashboard } from './UsageDashboard';
import { ChatMessages, Message } from './ChatMessages';
import { ChatInput } from './ChatInput';
import { DocumentSidebar, Session } from './DocumentSidebar';
import { LlmModal } from './LlmModal';
import { MobileSheet } from './MobileSheet';
import { agentFetch } from '../../utils/agentFetch';

interface UserInfo {
  role: string;
  org_id: string;
  display_name: string;
  email: string;
}

interface OrgSpend {
  org_name: string;
  total_spend_gbp: number;
  spend_cap_gbp: number;
}

interface Props {
  userInfo: UserInfo | null;
}

export const ChatInterface: React.FC<Props> = ({ userInfo }) => {
  const { streamText, isStreaming, send } = useAgentStream();

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showLlmModal, setShowLlmModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  const [sessionTitle, setSessionTitle] = useState<string | null>(null);
  const [showMobileSheet, setShowMobileSheet] = useState(false);
  const [capExceeded, setCapExceeded] = useState(false);
  const [capMessage, setCapMessage] = useState('');
  const [orgSpend, setOrgSpend] = useState<OrgSpend | null>(null);
  const [latestUsage, setLatestUsage] = useState<AgentUsage | null>(null);
  const [sessionTotals, setSessionTotals] = useState({
    claude: { tokens: 0, cost: 0 },
    gemini: { tokens: 0, cost: 0 },
  });
  const isSuperAdmin = userInfo?.role === 'super_admin';

  // Load sessions on mount
  const loadSessions = useCallback(async () => {
    try {
      const r = await agentFetch('/api/sessions');
      if (r.ok) setSessions(await r.json());
    } catch (_) {}
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Check spend cap on mount
  useEffect(() => {
    agentFetch('/admin/api/my-org-spend')
      .then(r => r.ok ? r.json() : null)
      .then((data: OrgSpend | null) => {
        if (data) setOrgSpend(data);
        if (data && data.total_spend_gbp >= data.spend_cap_gbp) {
          setCapExceeded(true);
          const cap = data.spend_cap_gbp != null ? `£${data.spend_cap_gbp.toFixed(2)}` : 'its limit';
          setCapMessage(`Your organisation has reached its ${cap} spend cap. Please contact rav@therdf.co.uk to increase it.`);
        }
      })
      .catch(() => {});
  }, []);

  const autoSave = useCallback(async (history: Message[], title: string) => {
    if (!history.length) return;
    try {
      await agentFetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          title,
          messages: history.map(m => ({ role: m.role, content: m.content })),
        }),
      });
    } catch (_) {}
  }, [sessionId]);

  const handleSend = useCallback(() => {
    if (isStreaming || capExceeded || !inputValue.trim()) return;
    if (selectedIds.size === 0) {
      alert('Please select at least one document folder from the library to proceed.');
      return;
    }
    setPendingMessage(inputValue.trim());
    setInputValue('');
    setShowLlmModal(true);
  }, [isStreaming, capExceeded, inputValue, selectedIds]);

  const handleModelSelect = useCallback(async (model: string | null) => {
    setShowLlmModal(false);
    if (!model || !pendingMessage) { setPendingMessage(null); return; }

    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: pendingMessage,
    };
    const title = sessionTitle || pendingMessage.slice(0, 70);
    if (!sessionTitle) setSessionTitle(title);

    setMessages(prev => [...prev, userMsg]);

    const historyForApi = messages.map(m => ({ role: m.role, content: m.content }));
    historyForApi.push({ role: 'user', content: pendingMessage });

    await send(
      {
        message: pendingMessage,
        filters: Array.from(selectedIds),
        llm: model,
        history: messages.map(m => ({ role: m.role, content: m.content })),
      },
      {
        onDone: (rawText, usageData, spendWarning) => {
          const aiMsg: Message = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: rawText,
            model,
            spendWarning: spendWarning || undefined,
          };
          setMessages(prev => {
            const updated = [...prev, aiMsg];
            autoSave(updated, title);
            loadSessions();
            return updated;
          });
          if (usageData) {
            setLatestUsage(usageData);
            setSessionTotals(prev => {
              const isGemini = model.includes('gemini');
              const bucket = isGemini ? 'gemini' : 'claude';
              return {
                ...prev,
                [bucket]: {
                  tokens: prev[bucket].tokens + (usageData.total_tokens || 0),
                  cost: prev[bucket].cost + (usageData.cost_gbp || 0),
                },
              };
            });
          }
          if (usageData && usageData.cost_gbp > 0) {
            // Re-check cap after query
            agentFetch('/admin/api/my-org-spend')
              .then(r => r.ok ? r.json() : null)
              .then((data: OrgSpend | null) => {
                if (data) setOrgSpend(data);
                if (data && data.total_spend_gbp >= data.spend_cap_gbp) {
                  setCapExceeded(true);
                  const cap = `£${data.spend_cap_gbp.toFixed(2)}`;
                  setCapMessage(`Your organisation has reached its ${cap} spend cap. Please contact rav@therdf.co.uk to increase it.`);
                }
              })
              .catch(() => {});
          }
        },
        onError: (code, msg) => {
          let displayMsg = msg;
          if (code === 'SPEND_CAP_EXCEEDED' || code === '402') {
            displayMsg = 'Your organisation has reached its spend cap. Queries are temporarily blocked. Please contact rav@therdf.co.uk to increase the cap.';
            setCapExceeded(true);
          } else if (code === '429') {
            displayMsg = 'Please wait a moment before sending another query.';
          } else if (code === '413') {
            displayMsg = 'Your query selected too many documents and exceeded the 50,000 token limit. Please deselect some folders and try again.';
          } else if (code === 'overloaded_error' || msg.toLowerCase().includes('overload')) {
            displayMsg = 'The AI service is currently overloaded. Please wait a moment and try again.';
          }
          const errMsg: Message = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: displayMsg,
          };
          setMessages(prev => [...prev, errMsg]);
        },
      },
    );

    setPendingMessage(null);
  }, [pendingMessage, messages, selectedIds, send, autoSave, loadSessions, sessionTitle]);

  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setSessionId(crypto.randomUUID());
    setSessionTitle(null);
    loadSessions();
  }, [loadSessions]);

  const handleSessionRestore = useCallback(async (id: string) => {
    try {
      const r = await agentFetch(`/api/sessions/${id}`);
      if (!r.ok) return;
      const session = await r.json();
      setSessionId(id);
      setSessionTitle(session.title);
      const restored: Message[] = (session.messages || []).map((m: any) => ({
        id: crypto.randomUUID(),
        role: m.role,
        content: m.content,
      }));
      setMessages(restored);
    } catch (_) {}
  }, []);

  return (
    <div className="ai-chat-wrapper">
      <div className="ai-chat-layout">
        {/* Sidebar — hidden on mobile */}
        <DocumentSidebar
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onSessionRestore={handleSessionRestore}
          sessions={sessions}
          onSessionsReload={loadSessions}
        />

        {/* Main chat area */}
        <div className="ai-chat-main">
          <ChatMessages
            messages={messages}
            streamText={streamText}
            isStreaming={isStreaming}
            onSuggestionClick={text => { setInputValue(text); }}
            onNewConversation={handleNewConversation}
          />
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSend}
            onOpenMobileFilter={() => setShowMobileSheet(true)}
            isStreaming={isStreaming}
            capExceeded={capExceeded}
            capMessage={capMessage}
          />
        </div>

        {/* LLM selection modal */}
        {showLlmModal && (
          <LlmModal
            isSuperAdmin={isSuperAdmin}
            onSelect={handleModelSelect}
            orgSpend={orgSpend}
          />
        )}

        {/* Mobile document filter sheet */}
        <MobileSheet
          open={showMobileSheet}
          onClose={() => setShowMobileSheet(false)}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onSessionRestore={handleSessionRestore}
          sessions={sessions}
          onSessionsReload={loadSessions}
        />
      </div>

      {/* Usage bar — spans full width below both columns */}
      <UsageDashboard
        latestUsage={latestUsage}
        sessionTotals={sessionTotals}
        isSuperAdmin={isSuperAdmin}
        orgSpend={orgSpend}
      />
    </div>
  );
};
